import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Formik } from 'formik';
import _ from 'lodash';

import { Drawer, Grid, IconButton, makeStyles } from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';

import CustomTextField from '../../components/TextField';
import CustomDateField from '../../components/DateField';
import PageWrapper from '../../components/PageWrapper';
import SaveBox from '../../components/SaveBox';

import { adictiveDrawerInitialValues } from '../../form-configs/permissionaries-initial-values';
import { fetchDocumentTypes } from 'app/store/slices/forms';
import { permissionariesAddictivesValidationSchema } from '../../form-configs/validation-schemas';
import CustomMaskField from '../../components/MaskField';
import { useSearchProcess } from '../../hooks/useSearchProcess';
import { validateFormikForm } from 'app/utils/forms/functions';
import DefaultTable from '../../components/DefaultTable';
import { attachmentColumns } from '../../table-configs/columns-schemas';
import AddButton from '../../components/AddButton';
import useShowFormErrors from '../../hooks/useShowFormErrors';
import filesEndpoint from '../../../../api/services/filesEndpoint';
import FindRequest from './FindRequest';
import { SERVICES_CODES } from '../../../../config/services';
import { STATUS_CODES } from '../../../../config/status';

const DEFAULT_ATTACHMENT = {
    cdDocumentoAditivo: null,
    uid: null,
    canEdit: false,
    lockSelect: false,
    arquivo: '',
    idFile: '',
    deDescricao: '',
    fileName: '',
    tipoDocumento: null,
};

const AddAddictives = ({ onClose, onSave, selectedAddictive, contextClearer }) => {
    // eslint-disable-next-line no-unused-vars
    const classes = useStyles();
    const dispatch = useDispatch();
    const { formElement, scrollToErrors } = useShowFormErrors();

    const [loading, setLoading] = useState(true);
    const [loadingDocs, setLoadingDocs] = useState(true);

    const [openDrawer, setOpenDrawer] = useState(false);
    const servicesFilter = [
        { cdServico: SERVICES_CODES.SLIC_INCLUSAO_CONDUTOR_SUBSTITUTO },
        { cdServico: SERVICES_CODES.SLIC_SUBSTITUICAO_VEICULO },
        { cdServico: SERVICES_CODES.SLIC_TRANSFERENCIA_PERMISSAO },
        { cdServico: SERVICES_CODES.SLIC_PRORROGACAO_PRAZO },
    ];

    const requestsfilters = {
        situacoes: [
            { cdSituacao: STATUS_CODES.EM_ANALISE },
            { cdSituacao: STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS },
        ],
        servicos: servicesFilter,
    };

    const [uploadingDocument, setUploadingDocument] = useState(false);

    const [addictiveAttachments, setAddictiveAttachments] = useState([]);
    const [formInitials, setFormInitials] = useState(adictiveDrawerInitialValues);

    const documentTypes = useSelector((state) => state.forms.documentTypes);
    const selectedPermissionary = useSelector((state) => state.line.selectedPermissionary);
    const selectedLine = useSelector((state) => state.line.selected);

    const {
        nuProcesso,
        setNuProcesso,
        nuSolicitacao,
        setNuSolicitacao,
        searchByProcess,
        searchByRequest,
        blockProcess,
        blockRequest,
        fetchingProcess,
    } = useSearchProcess({ throwError: false, lockRequest: false });

    useEffect(() => {
        return () => contextClearer();
    }, [contextClearer]);

    useEffect(() => {
        (async () => {
            setLoadingDocs(true);
            if (selectedAddictive && Object.keys(selectedAddictive).length > 0) {
                setFormInitials(() => selectedAddictive);

                if (selectedAddictive.nuProcesso) {
                    setNuProcesso(selectedAddictive.nuProcesso);
                }

                if (selectedAddictive.nuSolicitacao) {
                    setNuSolicitacao(selectedAddictive.nuSolicitacao);
                }

                if (selectedAddictive.documentos.length > 0) {
                    const temp = [];
                    for (const doc of selectedAddictive.documentos) {
                        let fileName = '';
                        if (doc.idFile && (!doc.fileName || doc.fileName === '')) {
                            const response = await filesEndpoint.getFileData(doc.idFile);
                            if (response && response.status === 200) {
                                fileName = response.data.name;
                            }
                        }
                        temp.push({ fileName, ...doc, canEdit: true, lockSelect: true });
                    }
                    setAddictiveAttachments(temp);
                }
            }
            setLoadingDocs(false);
        })();
    }, [selectedAddictive, setNuProcesso, setNuSolicitacao]);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                if (documentTypes.length === 0) await dispatch(fetchDocumentTypes());
            } catch (error) {
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleSelectRequest = async (data, formikBag) => {
        setOpenDrawer(false);
        await searchByRequest(data.nuSolicitacaoFmt);
        formikBag.setFieldTouched('nuProcesso');
    };

    async function validateFormAndCallFunction(formProps, cb) {
        formProps.setFieldTouched('nuProcesso');
        validateFormikForm(formProps, permissionariesAddictivesValidationSchema).then((res) => {
            if (Object.keys(res).length > 0) {
                scrollToErrors();
            } else {
                if (cb) {
                    cb();
                }
            }
        });
    }

    const handleSaveAddictive = (values) => {
        if (!onSave) return;
        const valuesCopy = _.cloneDeep(values);
        valuesCopy.nuProcesso = nuProcesso;
        valuesCopy.nuSolicitacao = nuSolicitacao;
        valuesCopy.cdLinha = selectedLine.cdLinha;
        valuesCopy.cdLinhaPermissionario = selectedPermissionary.cdLinhaPermissionario;
        valuesCopy.documentos = addictiveAttachments;
        onSave(valuesCopy);
        onClose();
    };

    return (
        <PageWrapper
            maxWidth="md"
            title="Aditivos"
            isPlain
            actionButton={
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            }>
            <Formik
                enableReinitialize
                validateOnBlur
                onSubmit={handleSaveAddictive}
                initialValues={formInitials}
                validationSchema={permissionariesAddictivesValidationSchema}>
                {(formikProps) => {
                    return (
                        <>
                            <Grid container spacing={4} ref={formElement}>
                                <Grid item xs={12} container>
                                    <Grid item xs={5}>
                                        <Field
                                            component={CustomTextField}
                                            name="deLinha"
                                            label="Linha"
                                            value={selectedLine?.deDescricao || ''}
                                            disabled
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            name="nuProcessoSei"
                                            component={CustomTextField}
                                            label="Número do Processo SEI"
                                            inputProps={{
                                                maxLength: 30,
                                            }}
                                            validate={(value) => {
                                                if (!value) {
                                                    const clearNuprocesso = nuProcesso.trim().replace(/\D/g, '');
                                                    if (!clearNuprocesso) {
                                                        return 'Número de processo obrigatório';
                                                    }
                                                }
                                            }}
                                            onBlur={() => {
                                                formikProps.setFieldTouched('nuProcesso');
                                                formikProps.setFieldTouched('nuProcessoSei');
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            disabled={blockRequest}
                                            component={CustomMaskField}
                                            variant="filled"
                                            fullWidth
                                            value={nuSolicitacao}
                                            mask={'999999/9999'}
                                            name="solicitacao"
                                            label="Solicitação"
                                            onBlur={(e) => searchByRequest(e.target.value)}
                                            onChange={(e) => setNuSolicitacao(e.target.value)}
                                            onClickButton={() => setOpenDrawer(true)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            name="nuProcesso"
                                            component={CustomMaskField}
                                            loading={fetchingProcess}
                                            disabled={blockProcess}
                                            value={nuProcesso}
                                            label="Número do Processo"
                                            mask={'9999.9999/999999'}
                                            validate={() => {
                                                if (!formikProps.values.nuProcessoSei) {
                                                    const clearNuprocesso = nuProcesso.trim().replace(/\D/g, '');
                                                    if (!clearNuprocesso) {
                                                        return 'Número de processo obrigatório';
                                                    }
                                                }
                                            }}
                                            error={
                                                formikProps.errors['nuProcesso'] && formikProps.touched['nuProcesso']
                                            }
                                            helperText={
                                                formikProps.touched['nuProcesso'] && formikProps.errors['nuProcesso']
                                            }
                                            onBlur={(e) => {
                                                formikProps.setFieldTouched('nuProcesso');
                                                formikProps.setFieldTouched('nuProcessoSei');
                                                searchByProcess(e.target.value);
                                            }}
                                            onChange={(e) => setNuProcesso(e.target.value)}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomTextField}
                                            name="nuTermo"
                                            label="Nº Termo de Permissão"
                                            value={selectedPermissionary?.nuTermo || ''}
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomTextField}
                                            label="Tipo de termo"
                                            name="cdTipoDocumento"
                                            select
                                            loading={loading}
                                            disabled={loading}
                                            SelectProps={{
                                                native: true,
                                            }}>
                                            <option value="">Selecione...</option>
                                            {documentTypes.map((docType, index) => (
                                                <option
                                                    key={`type-${docType?.cdTipoDocumento || index}`}
                                                    value={docType?.cdTipoDocumento}>
                                                    {docType.nmTipoDocumento}
                                                </option>
                                            ))}
                                        </Field>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Field component={CustomTextField} name="nuAditivo" label="Nº do Aditivo" />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Field component={CustomDateField} label="Data de emissão" name="dtEmissao" />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomDateField}
                                            label="Data de publicação"
                                            name="dtPublicacao"
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} container spacing={2}>
                                    <Grid item xs={12}>
                                        <Field
                                            component={CustomTextField}
                                            name="deObservacao"
                                            label="Observação"
                                            multiline
                                            minRows={4}
                                            inputProps={{
                                                maxLength: 5000,
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <DefaultTable
                                        title="Arquivos"
                                        columns={attachmentColumns}
                                        rows={addictiveAttachments.filter((attc) => !attc.boExcluido)}
                                        loading={loadingDocs}
                                        onDelete={(e, data) => {
                                            if (data.cdDocumentoAditivo === null) {
                                                setAddictiveAttachments((prevDocs) => {
                                                    return prevDocs.filter((doc) => {
                                                        return doc.uid !== data.uid;
                                                    });
                                                });
                                            } else {
                                                setAddictiveAttachments((prevDocs) => {
                                                    return prevDocs.map((doc) =>
                                                        doc.idFile === data.idFile ? { ...doc, boExcluido: true } : doc,
                                                    );
                                                });
                                            }
                                        }}
                                        entryProps={{
                                            idProperty: 'cdAditivo',
                                            idValue: selectedAddictive?.cdAditivo,
                                            uploadingState: uploadingDocument,
                                            setUploadingState: setUploadingDocument,
                                            documentList: documentTypes,
                                            onSelectDocType: (newDocObj, index) => {
                                                setAddictiveAttachments((prevDocs) => {
                                                    let temp = [...prevDocs];
                                                    temp[index] = {
                                                        ...temp[index],
                                                        tipoDocumento: newDocObj,
                                                        boAlterado: true,
                                                        canEdit: Boolean(newDocObj),
                                                    };
                                                    return temp;
                                                });
                                            },
                                            onDeleteFile: (docId, index) => {
                                                setAddictiveAttachments((prevDocs) => {
                                                    let temp = [...prevDocs];
                                                    temp[index] = {
                                                        ...temp[index],
                                                        idFile: docId,
                                                        boAlterado: true,
                                                        lockSelect: false,
                                                    };
                                                    return temp;
                                                });
                                            },
                                            onSelectFile: (data, index) => {
                                                setAddictiveAttachments((prevDocs) => {
                                                    let temp = [...prevDocs];
                                                    let uid = temp[index].uid;
                                                    let cdDocumentoAditivo = temp[index].cdDocumentoAditivo;
                                                    temp[index] = { ...data, uid, cdDocumentoAditivo };
                                                    return temp;
                                                });
                                            },
                                        }}
                                        actionButton={
                                            <AddButton
                                                className={classes.addButton}
                                                onClick={() => {
                                                    let newDocument = { ...DEFAULT_ATTACHMENT, uid: uuid() };
                                                    setAddictiveAttachments((prev) => [...prev, newDocument]);
                                                }}>
                                                Adicionar
                                            </AddButton>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <SaveBox
                                middleButtonLabel="Salvar"
                                onSave={() => validateFormAndCallFunction(formikProps, formikProps.handleSubmit)}
                                onCancel={onClose}
                                firstButtonDisabled={fetchingProcess}
                                middleButtonDisabled={fetchingProcess}
                                lastButtonDisabled={fetchingProcess}
                                removeSpacing
                            />
                            <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)}>
                                <FindRequest
                                    onSave={(values) => handleSelectRequest(values, formikProps)}
                                    onClose={() => setOpenDrawer(false)}
                                    filters={requestsfilters}
                                    hasSearch={true}
                                />
                            </Drawer>
                        </>
                    );
                }}
            </Formik>
        </PageWrapper>
    );
};

export default AddAddictives;

const useStyles = makeStyles((theme) => ({
    addButton: {
        borderRadius: '25px',
        padding: '5px 20px',
        border: '1px solid #ccc',
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        backgroundColor: '#fff',
        textTransform: 'uppercase',
        fontWeight: 'bold',
        fontFamily: theme.typography.fontFamily,
    },
    centerProgress: {
        textAlign: 'center',
    },
}));
